import { isString } from "./typeHelpers";
import markdownit from 'markdown-it';
import * as IncrementalDOM from 'incremental-dom/dist/incremental-dom-cjs';
import markdownitIncrementalDOM from 'markdown-it-incremental-dom';

export function copyToClipboard(text?: string, convertMarkdown: boolean = false): Promise<void> {
    if (!text || !isString(text)) return Promise.reject(new Error("Invalid input"));

    return new Promise(async (resolve, reject) => {
        try {
            let contentToCopy = text;
            let htmlContent = '';

            // If markdown conversion is needed
            if (convertMarkdown) {
                const md = markdownit({
                    html: true,
                    linkify: true,
                    typographer: true,
                }).use(markdownitIncrementalDOM, IncrementalDOM);

                htmlContent = md.render(text);

                const tableStyle = `
                    <style>
                        table, th, td {
                            border: 1px solid black;
                            border-collapse: collapse;
                            padding: 8px;
                        }
                    </style>
                `;

                htmlContent = tableStyle + htmlContent;
            }

            if ('clipboard' in navigator) {
                if (convertMarkdown) {
                    // Copy both HTML and plain text when markdown is converted
                    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
                    const plainTextBlob = new Blob([text], { type: 'text/plain' });

                    const data = [
                        new ClipboardItem({
                            'text/html': htmlBlob,
                            'text/plain': plainTextBlob
                        })
                    ];

                    await navigator.clipboard.write(data);
                } else {
                    await navigator.clipboard.writeText(contentToCopy);
                }
                resolve();
            } else {
                const temp = document.createElement("textarea");

                temp.style.pointerEvents = "none";
                temp.style.opacity = "0";
                document.body.appendChild(temp);

                temp.value = contentToCopy;
                temp.select();
                const didCopy = document.execCommand("copy");

                document.body.removeChild(temp);

                didCopy ? resolve() : reject();
            }
        } catch (error) {
            reject();
        }
    });
}


/**
 * Renames an image file, typically originating from the clipboard, by appending a unique timestamp
 * to its base name. This is useful to avoid name collisions for pasted images that often share a default name.
 *
 * @param {File} file - The original image file object.
 * @returns {File} A new File object with the same contents and metadata, but a unique name.
 *
 * @example
 * const renamedFile = renameClipboardImageFile(file);
 * // renamedFile.name === 'image-1729482342342.png'
 */
export function renameClipboardImageFile(file: File) {
    if (!file || !file.type.startsWith('image/')) return file;

    // Split filename and extension
    const match = file.name.match(/^(.*?)(\.[^.]+)?$/);
    const base = match?.[1] || 'image';
    const ext = match?.[2] || `.${file.type.split('/')[1] || 'png'}`;

    // Add a prefix or suffix (timestamp/random string)
    const unique = `${base}-${Date.now()}${ext}`;

    return new File([file], unique, {
        type: file.type,
        lastModified: file.lastModified
    });
}

/**
 * Extracts embedded image files from RTF (Rich Text Format) clipboard data, specifically images
 * pasted as hex-encoded blocks (such as from Word, Outlook, etc.). Returns each found image as a File object.
 *
 * @param {DataTransfer | null} clipboardData - The DataTransfer object from a clipboard event.
 * @returns {File[]} An array of File objects extracted from the RTF clipboard data (may be empty).
 *
 * @example
 * document.addEventListener('paste', (e) => {
 *   const files = extractFilesFromRtfClipboard(e.clipboardData);
 *   // files will contain File objects for each image found in RTF data.
 * });
 *
 * Details:
 * - Searches the RTF data for \pict blocks with pngblip or jpegblip (PNG/JPEG images).
 * - Decodes the hex data to binary, infers the filename (if present), and creates File objects.
 */
export function extractFilesFromRtfClipboard(clipboardData: DataTransfer | null): File[] {
    if (!clipboardData) return [];

    const rtfData = clipboardData.getData("text/rtf");
    if (!rtfData) return [];

    // Converts a hex string (possibly containing spaces/newlines) to a Uint8Array
    const convertHexToUint8Array = (hex: string): Uint8Array => {
        const cleanHex = hex.replace(/[^\da-fA-F]/g, '');
        const byteArray = new Uint8Array(cleanHex.length / 2);
        for (let i = 0; i < cleanHex.length; i += 2) {
            byteArray[i / 2] = parseInt(cleanHex.substr(i, 2), 16);
        }
        return byteArray;
    };

    // Regex to find \pict RTF blocks with optional header and hex image data
    const pictRegex = /{\\pict([\s\S]+?)?(?:\\(pngblip|jpegblip))[\s\S]+?}(?:\s*([a-fA-F0-9\s]+))}/g;
    // Regex to extract wzName from header (if present), as suggested image name
    const wzNameRegex = /\\sp\{\\sn wzName\}\{\\sv ([^}]+)\}/;

    const files: File[] = [];

    let match: RegExpExecArray | null;
    while ((match = pictRegex.exec(rtfData)) !== null) {
        const header = match[1] || "";
        const format = match[2];
        const hex = match[3];

        if (!format || !hex) continue;

        const mime = format === "pngblip" ? "image/png" : "image/jpeg";
        const nameMatch = wzNameRegex.exec(header);
        // Use wzName if available, otherwise fallback to 'pasted-image'
        const name = (nameMatch?.[1] || "pasted-image") + (mime === "image/png" ? ".png" : ".jpg");

        const bytes = convertHexToUint8Array(hex);
        const blob = new Blob([bytes], { type: mime });
        const file = new File([blob], name, { type: mime });

        files.push(file);
    }

    return files;
}

/**
 * Detects whether clipboard content originates from Microsoft Word by checking for
 * Word-specific file MIME types or namespace declarations in the clipboard data.
 *
 * @param {DataTransfer | null} clipboardData - The DataTransfer object from a clipboard event.
 * @returns {boolean} True if the clipboard content is from Microsoft Word, false otherwise.
 *
 * @example
 * document.addEventListener('paste', (e) => {
 *   if (isClipboardFromWord(e.clipboardData)) {
 *     // Handle Word-specific paste behavior
 *   }
 * });
 */
export function isClipboardFromWord(clipboardData: DataTransfer | null): boolean {
    if (!clipboardData) {
        return false;
    }

    // Check for Word file MIME types in actual file items
    const items = clipboardData.items || [];
    for (const item of items) {
        if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) {
                const mimeType = file.type;
                if (mimeType === 'application/msword'  // .doc
                    || mimeType === 'application/vnd.ms-word'  // alternative .doc
                    || mimeType === 'application/x-msword'  // another alternative .doc
                    || mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {  // .docx
                    return true;
                }
            }
        }
    }

    // Check for Word namespace in HTML content (for copied text/images from Word)
    if (clipboardData.types.includes("text/html")) {
        const htmlContent = clipboardData.getData("text/html");
        return htmlContent.includes("xmlns:w=") && htmlContent.includes("office:word");
    }

    return false;
}

/**
 * Detects whether clipboard content originates from Microsoft Excel by checking for
 * Excel-specific file MIME types or namespace declarations in the clipboard data.
 *
 * @param {DataTransfer | null} clipboardData - The DataTransfer object from a clipboard event.
 * @returns {boolean} True if the clipboard content is from Microsoft Excel, false otherwise.
 *
 * @example
 * document.addEventListener('paste', (e) => {
 *   if (isClipboardFromExcel(e.clipboardData)) {
 *     // Handle Excel-specific paste behavior (e.g., strip embedded images)
 *   }
 * });
 */
export function isClipboardFromExcel(clipboardData: DataTransfer | null): boolean {
    if (!clipboardData) {
        return false;
    }

    // Check for Excel file MIME types in actual file items
    const items = clipboardData.items || [];
    for (const item of items) {
        if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) {
                const mimeType = file.type;
                if (mimeType === 'application/vnd.ms-excel'  // .xls
                    || mimeType === 'application/x-msexcel'  // alternative .xls
                    || mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {  // .xlsx
                    return true;
                }
            }
        }
    }

    // Check for Excel namespace in HTML content (for copied cell content)
    if (clipboardData.types.includes("text/html")) {
        const htmlContent = clipboardData.getData("text/html");
        return htmlContent.includes("xmlns:x=");
    }

    return false;
}

/**
 * Detects whether clipboard content originates from Microsoft PowerPoint by checking for
 * PowerPoint-specific file MIME types or meta tags in the clipboard data.
 *
 * @param {DataTransfer | null} clipboardData - The DataTransfer object from a clipboard event.
 * @returns {boolean} True if the clipboard content is from Microsoft PowerPoint, false otherwise.
 *
 * @example
 * document.addEventListener('paste', (e) => {
 *   if (isClipboardFromPowerPoint(e.clipboardData)) {
 *     // Handle PowerPoint-specific paste behavior (e.g., ignore unwanted images)
 *   }
 * });
 */
export function isClipboardFromPowerPoint(clipboardData: DataTransfer | null): boolean {
    if (!clipboardData) {
        return false;
    }

    // Check for PowerPoint file MIME types in actual file items
    const items = clipboardData.items || [];
    for (const item of items) {
        if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) {
                const mimeType = file.type;
                if (mimeType === 'application/vnd.ms-powerpoint'  // .ppt
                    || mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {  // .pptx
                    return true;
                }
            }
        }
    }

    // Check for PowerPoint meta tags in HTML content (for copied slide content)
    if (clipboardData.types.includes("text/html")) {
        const htmlContent = clipboardData.getData("text/html");
        return htmlContent.includes("ProgId content=PowerPoint.Slide");
    }

    return false;
}